<template>
<div class="ins-root" :style="themePageStyle">

  <!-- Topbar -->
  <nav class="ins-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <back-button class="me-2" />
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Insurance &amp; Finance</span>
    </div>
    <div class="d-flex gap-2 flex-wrap">
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/insurance/provider')"><i class="mdi mdi-shield-account me-1"></i>Insurance partner</button>
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/finance/provider')"><i class="mdi mdi-bank-outline me-1"></i>Finance partner</button>
      <button class="btn btn-sm btn-outline-warning d-none d-md-inline-block" @click="$router.push('/dashboard')"><i class="mdi mdi-view-dashboard me-1"></i>Dashboard</button>
      <button class="btn btn-sm btn-warning" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i>Marketplace</button>
    </div>
  </div></nav>

  <div class="container ins-container">

    <!-- Hero -->
    <div class="text-center mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">🛡️ Protect &amp; Finance Your Herd</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;max-width:640px;margin:0 auto;">
        Insurance from KLIP, APA, UAP &amp; Jubilee — and finance from banks, MFIs, SACCOs and fintechs that
        fund livestock acquisition, feed, lease-to-own and savings. Browse, apply or buy cover in minutes.
      </p>
    </div>

    <!-- Tabs -->
    <div class="ins-tabs mb-4">
      <button class="ins-tab" :class="{active: tab==='browse'}" @click="tab='browse'">
        <i class="mdi mdi-shield-search me-1"></i>Insurance
      </button>
      <button class="ins-tab" :class="{active: tab==='finance'}" @click="tab='finance'; loadFinanceProducts()">
        <i class="mdi mdi-bank-outline me-1"></i>Finance
      </button>
      <button class="ins-tab" :class="{active: tab==='policies'}" @click="tab='policies'; loadPolicies()">
        <i class="mdi mdi-shield-check me-1"></i>My Policies
        <span v-if="policies.length" class="ins-tab-badge">{{ policies.length }}</span>
      </button>
      <button class="ins-tab" :class="{active: tab==='claims'}" @click="tab='claims'; loadClaims()">
        <i class="mdi mdi-file-document-edit me-1"></i>My Claims
        <span v-if="claims.length" class="ins-tab-badge">{{ claims.length }}</span>
      </button>
      <button class="ins-tab" :class="{active: tab==='finance-apps'}" @click="tab='finance-apps'; loadMyFinanceApps()">
        <i class="mdi mdi-file-document-multiple-outline me-1"></i>My Finance Applications
        <span v-if="myFinanceApps.length" class="ins-tab-badge">{{ myFinanceApps.length }}</span>
      </button>
    </div>

    <!-- ===================== BROWSE PRODUCTS ===================== -->
    <div v-if="tab==='browse'">

      <!-- Type filter chips -->
      <div class="d-flex gap-2 mb-4 flex-wrap">
        <button class="ins-chip" :class="{active: typeFilter===''}"          @click="typeFilter=''">All Types</button>
        <button class="ins-chip" :class="{active: typeFilter==='livestock'}"  @click="typeFilter='livestock'">🐄 Livestock</button>
        <button class="ins-chip" :class="{active: typeFilter==='comprehensive'}" @click="typeFilter='comprehensive'">🛡️ Comprehensive</button>
        <button class="ins-chip" :class="{active: typeFilter==='transit'}"     @click="typeFilter='transit'">🚛 Transit</button>
        <button class="ins-chip" :class="{active: typeFilter==='theft'}"       @click="typeFilter='theft'">🔒 Theft</button>
        <button class="ins-chip" :class="{active: typeFilter==='pet'}"         @click="typeFilter='pet'">🐕 Pet</button>
        <button class="ins-chip" :class="{active: typeFilter==='index_based'}" @click="typeFilter='index_based'">🌦 Index-based</button>
      </div>

      <!-- Loading -->
      <div v-if="loadingProducts" class="text-center py-5">
        <div class="spinner-border text-warning"></div>
        <p class="mt-2" style="color:var(--fm-s,#6B6356);">Loading insurance products...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="!filteredProducts.length" class="text-center py-5">
        <i class="mdi mdi-shield-off-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No products in this category</h5>
        <p style="color:var(--fm-s,#6B6356);">Try a different filter or come back soon — providers are added regularly.</p>
      </div>

      <!-- Product grid -->
      <div v-else class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="p in filteredProducts" :key="p.id">
          <div class="ins-card">
            <div class="d-flex align-items-center gap-3 mb-2">
              <div class="ins-provider-logo">
                <img v-if="p.provider_logo" :src="p.provider_logo" />
                <span v-else>{{ (p.provider_name||'?').charAt(0) }}</span>
              </div>
              <div class="flex-fill" style="min-width:0;">
                <div class="ins-product-name">{{ p.name }}</div>
                <div class="ins-provider-name">{{ p.provider_name }}</div>
              </div>
              <span class="ins-type-tag">{{ typeLabel(p.insurance_type) }}</span>
            </div>

            <p class="ins-desc">{{ p.description }}</p>

            <!-- Coverage summary -->
            <div class="ins-coverage" v-if="coverageOf(p).covers.length">
              <div class="ins-coverage-label"><i class="mdi mdi-check-circle me-1" style="color:#50C878;"></i>Covers</div>
              <div class="d-flex flex-wrap gap-1 mb-2">
                <span class="ins-pill ok" v-for="c in coverageOf(p).covers" :key="'c-'+c">{{ humanize(c) }}</span>
              </div>
              <div v-if="coverageOf(p).excludes.length">
                <div class="ins-coverage-label"><i class="mdi mdi-close-circle me-1" style="color:#FF6B6B;"></i>Excludes</div>
                <div class="d-flex flex-wrap gap-1">
                  <span class="ins-pill bad" v-for="x in coverageOf(p).excludes" :key="'x-'+x">{{ humanize(x) }}</span>
                </div>
              </div>
            </div>

            <!-- Premium / duration -->
            <div class="ins-stats mt-3">
              <div>
                <div class="ins-stat-label">Premium</div>
                <div class="ins-stat-val">
                  <span v-if="p.flat_premium">KES {{ Number(p.flat_premium).toLocaleString() }}</span>
                  <span v-else>{{ Number(p.premium_rate_pct).toFixed(2) }}% / yr</span>
                </div>
              </div>
              <div>
                <div class="ins-stat-label">Duration</div>
                <div class="ins-stat-val">{{ p.duration_months }} mo</div>
              </div>
              <div>
                <div class="ins-stat-label">Waiting</div>
                <div class="ins-stat-val">{{ p.waiting_period_days }} d</div>
              </div>
            </div>

            <!-- Value range -->
            <div v-if="p.min_animal_value || p.max_animal_value" style="font-size:11px;color:var(--fm-m,#A09888);margin-top:8px;">
              <i class="mdi mdi-cash-multiple me-1"></i>Animal value:
              KES {{ Number(p.min_animal_value||0).toLocaleString() }} – {{ Number(p.max_animal_value||0).toLocaleString() }}
            </div>

            <button class="btn btn-warning w-100 mt-3" @click="openPurchase(p)">
              <i class="mdi mdi-shield-plus me-1"></i>Get Quote &amp; Buy
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== FINANCE BROWSE ===================== -->
    <div v-if="tab==='finance'">

      <!-- Type filter chips -->
      <div class="d-flex gap-2 mb-4 flex-wrap">
        <button class="ins-chip" :class="{active: financeTypeFilter===''}"                 @click="financeTypeFilter=''">All Products</button>
        <button class="ins-chip" :class="{active: financeTypeFilter==='livestock_asset'}"  @click="financeTypeFilter='livestock_asset'">🐄 Livestock Asset</button>
        <button class="ins-chip" :class="{active: financeTypeFilter==='working_capital'}"  @click="financeTypeFilter='working_capital'">💰 Working Capital</button>
        <button class="ins-chip" :class="{active: financeTypeFilter==='lease_to_own'}"     @click="financeTypeFilter='lease_to_own'">📋 Lease-to-Own</button>
        <button class="ins-chip" :class="{active: financeTypeFilter==='savings'}"          @click="financeTypeFilter='savings'">🏦 Savings</button>
      </div>

      <div v-if="loadingFinance" class="text-center py-5">
        <div class="spinner-border text-warning"></div>
        <p class="mt-2" style="color:var(--fm-s,#6B6356);">Loading finance products...</p>
      </div>

      <div v-else-if="!filteredFinanceProducts.length" class="text-center py-5">
        <i class="mdi mdi-bank-off-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No finance products in this category</h5>
        <p style="color:var(--fm-s,#6B6356);">Try a different filter or come back soon — banks, MFIs and SACCOs are being onboarded.</p>
        <button class="btn btn-outline-warning mt-2" @click="$router.push('/finance/provider')"><i class="mdi mdi-bank-plus me-1"></i>I'm a financier — list my products</button>
      </div>

      <div v-else class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="p in filteredFinanceProducts" :key="'fp-'+p.id">
          <div class="ins-card">
            <div class="d-flex align-items-center gap-3 mb-2">
              <div class="ins-provider-logo">
                <img v-if="p.provider_logo" :src="imgUrl(p.provider_logo)" />
                <span v-else>{{ (p.provider_name||'?').charAt(0) }}</span>
              </div>
              <div class="flex-fill" style="min-width:0;">
                <div class="ins-product-name">{{ p.name }}</div>
                <div class="ins-provider-name">{{ p.provider_name }} <span v-if="p.provider_institution_type" style="opacity:.6;">· {{ instLabel(p.provider_institution_type) }}</span></div>
              </div>
              <span class="ins-type-tag">{{ financeTypeLabel(p.product_type) }}</span>
            </div>

            <div v-if="p.provider_kyc_status === 'pending'" class="ins-pending-banner">
              <i class="mdi mdi-clock-outline me-1"></i>Pending verification — KYC in progress
            </div>

            <p class="ins-desc" v-if="p.description">{{ p.description }}</p>

            <!-- Key stats -->
            <div class="ins-stats mt-2">
              <div>
                <div class="ins-stat-label">Rate (annual)</div>
                <div class="ins-stat-val">
                  <span v-if="p.interest_rate_pct">{{ Number(p.interest_rate_pct).toFixed(1) }}%</span>
                  <span v-else>—</span>
                  <span v-if="p.interest_type && p.interest_type !== 'none'" style="font-size:10px;color:var(--fm-m,#A09888);text-transform:uppercase;letter-spacing:.3px;margin-left:4px;">{{ p.interest_type }}</span>
                </div>
              </div>
              <div>
                <div class="ins-stat-label">Tenure</div>
                <div class="ins-stat-val">
                  <span v-if="p.tenure_min_months || p.tenure_max_months">{{ p.tenure_min_months || 1 }}–{{ p.tenure_max_months || '—' }} mo</span>
                  <span v-else>Flexible</span>
                </div>
              </div>
              <div>
                <div class="ins-stat-label">Fee</div>
                <div class="ins-stat-val">
                  <span v-if="p.processing_fee_pct">{{ Number(p.processing_fee_pct).toFixed(1) }}%</span>
                  <span v-else>—</span>
                </div>
              </div>
            </div>

            <!-- Amount range -->
            <div v-if="p.min_amount || p.max_amount" style="font-size:11px;color:var(--fm-m,#A09888);margin-top:8px;">
              <i class="mdi mdi-cash-multiple me-1"></i>
              KES {{ Number(p.min_amount||0).toLocaleString() }} – {{ p.max_amount ? Number(p.max_amount).toLocaleString() : 'Open' }}
            </div>

            <!-- Collateral / docs -->
            <div class="ins-coverage" style="margin-top:10px;">
              <div class="ins-coverage-label">
                <i v-if="p.collateral_required == 1 || p.collateral_required === true" class="mdi mdi-shield-key-outline me-1" style="color:#f1b44c;"></i>
                <i v-else class="mdi mdi-shield-off-outline me-1" style="color:#50C878;"></i>
                {{ (p.collateral_required == 1 || p.collateral_required === true) ? 'Collateral required' : 'No collateral' }}
                <span v-if="p.early_repayment_allowed == 1 || p.early_repayment_allowed === true" style="margin-left:8px;color:#50C878;font-weight:600;"><i class="mdi mdi-check me-1"></i>Early repayment OK</span>
              </div>
              <div v-if="(p.collateral_required == 1 || p.collateral_required === true) && p.collateral_details" style="font-size:11px;color:var(--fm-m,#A09888);margin-top:4px;">{{ p.collateral_details }}</div>
              <div v-if="parseRequirements(p.requirements).length" style="margin-top:6px;">
                <div class="ins-coverage-label" style="margin-bottom:4px;"><i class="mdi mdi-file-document-outline me-1" style="color:#BFA164;"></i>Documents needed</div>
                <div class="d-flex flex-wrap gap-1">
                  <span class="ins-pill" v-for="r in parseRequirements(p.requirements)" :key="'rq-'+p.id+'-'+r">{{ r }}</span>
                </div>
              </div>
            </div>

            <button class="btn btn-warning w-100 mt-3" @click="openFinanceApply(p)">
              <i class="mdi mdi-file-document-edit me-1"></i>Apply now
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== MY FINANCE APPLICATIONS ===================== -->
    <div v-if="tab==='finance-apps'">
      <div v-if="loadingMyFinanceApps" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
      <div v-else-if="!myFinanceApps.length" class="text-center py-5">
        <i class="mdi mdi-file-document-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No finance applications yet</h5>
        <p style="color:var(--fm-s,#6B6356);">Browse finance products and apply to a lender.</p>
        <button class="btn btn-warning" @click="tab='finance'; loadFinanceProducts();"><i class="mdi mdi-magnify me-1"></i>Browse Finance</button>
      </div>
      <div v-else class="row g-3">
        <div class="col-md-6" v-for="a in myFinanceApps" :key="'fa-'+a.id">
          <div class="ins-policy-card">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="ins-product-name">{{ a.product_name || 'Finance product' }}</div>
                <div class="ins-provider-name">{{ a.provider_name }}</div>
              </div>
              <span class="ins-status" :class="'st-'+a.status">{{ humanize(a.status) }}</span>
            </div>
            <div class="ins-policy-stats">
              <div>
                <div class="ins-stat-label">Requested</div>
                <div class="ins-stat-val">KES {{ Number(a.amount_requested||0).toLocaleString() }}</div>
              </div>
              <div v-if="a.approved_amount">
                <div class="ins-stat-label">Approved</div>
                <div class="ins-stat-val" style="color:#50C878;">KES {{ Number(a.approved_amount).toLocaleString() }}</div>
              </div>
              <div v-if="a.tenure_months">
                <div class="ins-stat-label">Tenure</div>
                <div class="ins-stat-val">{{ a.tenure_months }} mo</div>
              </div>
            </div>
            <div v-if="a.purpose" style="color:var(--fm-m,#A09888);font-size:12px;margin-top:8px;">{{ a.purpose }}</div>
            <div v-if="a.provider_notes" style="color:#BFA164;font-size:12px;margin-top:6px;background:rgba(191,161,100,.08);border:1px solid rgba(191,161,100,.2);padding:6px 10px;border-radius:6px;">
              <i class="mdi mdi-comment-text-outline me-1"></i>Lender note: {{ a.provider_notes }}
            </div>
            <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:6px;">Submitted {{ a.created_at }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== MY POLICIES ===================== -->
    <div v-if="tab==='policies'">
      <div v-if="loadingPolicies" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

      <div v-else-if="!policies.length" class="text-center py-5">
        <i class="mdi mdi-shield-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No policies yet</h5>
        <p style="color:var(--fm-s,#6B6356);">Browse products and protect your animals.</p>
        <button class="btn btn-warning" @click="tab='browse'"><i class="mdi mdi-magnify me-1"></i>Browse Insurance</button>
      </div>

      <div v-else class="row g-3">
        <div class="col-md-6" v-for="po in policies" :key="po.id">
          <div class="ins-policy-card">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="ins-product-name">{{ po.product_name }}</div>
                <div class="ins-provider-name">{{ po.provider_name }}</div>
                <div style="color:var(--fm-s,#6B6356);font-size:11px;font-family:monospace;">{{ po.policy_number }}</div>
              </div>
              <span class="ins-status" :class="'st-'+po.status">{{ humanize(po.status) }}</span>
            </div>

            <div class="ins-policy-stats">
              <div>
                <div class="ins-stat-label">Covered Value</div>
                <div class="ins-stat-val">KES {{ Number(po.covered_value||0).toLocaleString() }}</div>
              </div>
              <div>
                <div class="ins-stat-label">Premium</div>
                <div class="ins-stat-val">KES {{ Number(po.premium_amount||0).toLocaleString() }}</div>
              </div>
              <div>
                <div class="ins-stat-label">Coverage</div>
                <div class="ins-stat-val">{{ humanize(po.coverage_type) }}</div>
              </div>
            </div>

            <div class="d-flex justify-content-between mt-3" style="font-size:12px;color:var(--fm-m,#A09888);">
              <span><i class="mdi mdi-calendar-start me-1"></i>{{ po.start_date }}</span>
              <span><i class="mdi mdi-calendar-end me-1"></i>{{ po.end_date }}</span>
            </div>

            <div class="d-flex gap-2 mt-3">
              <button v-if="po.payment_status==='pending'" class="btn btn-sm btn-outline-warning flex-fill" @click="payPremium(po)">
                <i class="mdi mdi-cash-fast me-1"></i>Pay Premium
              </button>
              <button v-if="po.status==='active'" class="btn btn-sm btn-warning flex-fill" @click="openClaim(po)">
                <i class="mdi mdi-file-alert me-1"></i>Submit Claim
              </button>
              <span v-if="po.status==='claimed'" class="ins-status st-claimed flex-fill text-center">
                <i class="mdi mdi-clock me-1"></i>Claim under review
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== MY CLAIMS ===================== -->
    <div v-if="tab==='claims'">
      <div v-if="loadingClaims" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

      <div v-else-if="!claims.length" class="text-center py-5">
        <i class="mdi mdi-file-document-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No claims filed</h5>
        <p style="color:var(--fm-s,#6B6356);">When something happens to a covered animal, submit a claim from the My Policies tab.</p>
      </div>

      <div v-else class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="cl in claims" :key="cl.id">
          <div class="ins-policy-card">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="ins-product-name">{{ cl.product_name || cl.claim_type }}</div>
                <div style="color:var(--fm-s,#6B6356);font-size:11px;font-family:monospace;">{{ cl.claim_number }}</div>
              </div>
              <span class="ins-status" :class="'st-'+cl.status">{{ humanize(cl.status) }}</span>
            </div>

            <div style="color:var(--fm-m,#A09888);font-size:12px;margin-bottom:8px;">
              <i class="mdi mdi-tag-outline me-1"></i>{{ humanize(cl.claim_type) }}
              · <i class="mdi mdi-calendar me-1"></i>{{ cl.incident_date }}
            </div>

            <p class="ins-desc" style="margin-bottom:8px;">{{ cl.incident_description }}</p>

            <div class="d-flex justify-content-between" style="border-top:1px solid var(--fm-br,#1E1E1E);padding-top:8px;font-size:12px;">
              <div>
                <div class="ins-stat-label">Claimed</div>
                <div class="ins-stat-val">KES {{ Number(cl.claimed_amount||0).toLocaleString() }}</div>
              </div>
              <div v-if="cl.approved_amount">
                <div class="ins-stat-label">Approved</div>
                <div class="ins-stat-val" style="color:#50C878;">KES {{ Number(cl.approved_amount).toLocaleString() }}</div>
              </div>
              <div v-if="cl.payout_amount">
                <div class="ins-stat-label">Paid Out</div>
                <div class="ins-stat-val" style="color:#50C878;">KES {{ Number(cl.payout_amount).toLocaleString() }}</div>
              </div>
            </div>

            <div v-if="cl.review_notes" class="ins-review">
              <strong>Reviewer notes:</strong> {{ cl.review_notes }}
            </div>
            <div v-if="cl.rejection_reason" class="ins-review" style="border-color:#FF6B6B;">
              <strong>Rejection reason:</strong> {{ cl.rejection_reason }}
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- ===================== PURCHASE MODAL ===================== -->
  <div v-if="showPurchaseModal" class="ins-modal-overlay" @click.self="closePurchase">
    <div class="ins-modal">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;"><i class="mdi mdi-shield-plus me-2" style="color:#BFA164;"></i>Get Insurance Quote</h5>
        <button class="ins-x" @click="closePurchase">&times;</button>
      </div>

      <div v-if="selectedProduct" class="ins-modal-prod mb-3">
        <div class="ins-product-name">{{ selectedProduct.name }}</div>
        <div class="ins-provider-name">{{ selectedProduct.provider_name }} · {{ humanize(selectedProduct.insurance_type) }}</div>
      </div>

      <div class="mb-3">
        <label class="ins-label">Animal value (KES) <span style="color:#FF6B6B;">*</span></label>
        <input type="number" v-model.number="purchaseForm.coveredValue" class="form-control ins-input" :placeholder="valuePlaceholder" />
        <div v-if="valueWarning" class="ins-warn"><i class="mdi mdi-alert me-1"></i>{{ valueWarning }}</div>
      </div>

      <div class="mb-3">
        <label class="ins-label">Link to specific animal (optional)</label>
        <select v-model="purchaseForm.animalId" class="form-select ins-input">
          <option value="">— Cover not linked to a specific animal —</option>
          <option v-for="a in animals" :key="a.id" :value="a.id">
            {{ a.tag_number || a.name || a.id.slice(0,8) }} ({{ a.species_name || 'animal' }})
          </option>
        </select>
        <div v-if="loadingAnimals" style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:4px;">Loading your animals...</div>
      </div>

      <!-- Premium preview -->
      <div v-if="calculatedPremium" class="ins-premium-card mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div class="ins-stat-label">Annual Premium</div>
            <div class="ins-stat-val" style="font-size:22px;color:#BFA164;">KES {{ calculatedPremium.toLocaleString() }}</div>
          </div>
          <div class="text-end" style="font-size:11px;color:var(--fm-s,#6B6356);">
            <div v-if="!selectedProduct.flat_premium">{{ Number(selectedProduct.premium_rate_pct).toFixed(2) }}% × {{ Number(purchaseForm.coveredValue||0).toLocaleString() }}</div>
            <div>{{ selectedProduct.duration_months }} months cover</div>
            <div v-if="selectedProduct.waiting_period_days">{{ selectedProduct.waiting_period_days }}-day waiting period</div>
          </div>
        </div>
      </div>

      <div v-if="purchaseError" class="ins-warn mb-2"><i class="mdi mdi-alert-circle me-1"></i>{{ purchaseError }}</div>

      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary flex-fill" @click="closePurchase" :disabled="submitting">Cancel</button>
        <button class="btn btn-warning flex-fill" :disabled="!canPurchase || submitting" @click="submitPurchase">
          <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Creating policy...</span>
          <span v-else><i class="mdi mdi-shield-check me-1"></i>Create Policy</span>
        </button>
      </div>

      <p style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:10px;margin-bottom:0;">
        Policy is created in <strong>pending</strong> state. The provider activates cover once the premium is settled.
      </p>
    </div>
  </div>

  <!-- ===================== PAY PREMIUM (M-Pesa STK) ===================== -->
  <div v-if="showPayModal" class="ins-modal-overlay" @click.self="closePayModal">
    <div class="ins-modal ins-pay-modal">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;"><i class="mdi mdi-cellphone-message me-2" style="color:#50C878;"></i>Pay Premium via M-Pesa</h5>
        <button class="ins-x" @click="closePayModal">&times;</button>
      </div>

      <!-- Policy summary (always visible) -->
      <div v-if="payPolicy" class="ins-pay-card">
        <div class="ins-pay-row"><span class="ins-pay-label">Policy</span><span class="ins-pay-val mono">{{ payPolicy.policy_number }}</span></div>
        <div class="ins-pay-row"><span class="ins-pay-label">Product</span><span class="ins-pay-val">{{ payPolicy.product_name }}</span></div>
        <div class="ins-pay-row"><span class="ins-pay-label">Provider</span><span class="ins-pay-val">{{ payPolicy.provider_name }}</span></div>
        <div class="ins-pay-row" style="border-top:1px dashed var(--fm-br,#1E1E1E);padding-top:10px;margin-top:6px;">
          <span class="ins-pay-label">Premium due</span>
          <span class="ins-pay-val" style="color:#BFA164;font-size:18px;">KES {{ Number(payPolicy.premium_amount||0).toLocaleString() }}</span>
        </div>
      </div>

      <!-- Idle / submitting / failed-retry: phone input -->
      <div v-if="['idle','submitting','failed','cancelled','timeout'].indexOf(payState)>-1" class="mt-3">
        <label class="ins-label">M-Pesa phone number <span style="color:#FF6B6B;">*</span></label>
        <input type="tel" v-model="payPhone" class="form-control ins-input" placeholder="07XX XXX XXX" :disabled="payState==='submitting'" />

        <div v-if="['cancelled','failed','timeout'].indexOf(payState)>-1" class="ins-pay-state-bad">
          <i class="mdi mdi-alert-circle me-1"></i>
          <span v-if="payState==='cancelled'">Prompt was cancelled on the phone. Re-enter the number to try again.</span>
          <span v-else-if="payState==='timeout'">No response on the phone. Try again or use the paybill below.</span>
          <span v-else>{{ payError || 'Payment failed. Try a different phone or use the paybill below.' }}</span>
        </div>

        <button class="btn btn-warning w-100 mt-3" @click="submitPay" :disabled="!canPay || payState==='submitting'">
          <span v-if="payState==='submitting'"><span class="spinner-border spinner-border-sm me-1"></span>Sending STK prompt...</span>
          <span v-else><i class="mdi mdi-send me-1"></i>{{ ['cancelled','failed','timeout'].indexOf(payState)>-1 ? 'Resend M-Pesa Prompt' : 'Send M-Pesa Prompt' }}</span>
        </button>

        <div v-if="payShortcode" class="ins-paybill-fallback mt-3">
          <strong>Or pay manually:</strong>
          Lipa Na M-Pesa &rarr; {{ payShortcodeType==='till' ? 'Buy Goods' : 'Pay Bill' }}
          &rarr; <strong style="color:#BFA164;">{{ payShortcode }}</strong>
          &rarr; Account <strong style="color:#BFA164;">{{ payAccountRef }}</strong>
          &rarr; Amount <strong style="color:#BFA164;">KES {{ payAmount.toLocaleString() }}</strong>
        </div>
      </div>

      <!-- STK sent: polling state -->
      <div v-else-if="payState==='stk_sent'" class="ins-pay-pulse">
        <div class="ins-pulse">
          <div class="ins-pulse-ring"></div>
          <div class="ins-pulse-core"><i class="mdi mdi-cellphone"></i></div>
        </div>
        <h6 style="color:var(--fm-h,#F5F0E8);">Check your phone</h6>
        <p style="color:var(--fm-m,#A09888);font-size:13px;margin-bottom:10px;">
          M-Pesa prompt sent to <strong style="color:#BFA164;">{{ payPhone }}</strong>. Enter your PIN to authorise KES {{ payAmount.toLocaleString() }}.
        </p>

        <div class="ins-paybill-fallback">
          <strong>Prompt didn't arrive?</strong>
          Pay <strong style="color:#BFA164;">{{ payShortcode }}</strong> · Account <strong style="color:#BFA164;">{{ payAccountRef }}</strong> · KES {{ payAmount.toLocaleString() }}
        </div>

        <button class="btn btn-outline-secondary w-100 mt-3" @click="closePayModal">
          I'll come back later
        </button>
      </div>

      <!-- Success: auto-active -->
      <div v-else-if="payState==='success'" class="ins-pay-success">
        <div class="ins-pay-info-icon" style="background:rgba(80,200,120,.15);color:#50C878;">
          <i class="mdi mdi-check-decagram"></i>
        </div>
        <h6 style="color:#50C878;margin-bottom:6px;">Cover activated!</h6>
        <p style="color:var(--fm-m,#A09888);font-size:13px;margin-bottom:14px;">
          Your policy is now <strong style="color:#50C878;">active</strong> with {{ payPolicy && payPolicy.provider_name }}. We've sent you a confirmation email and SMS.
        </p>
        <button class="btn btn-warning w-100" @click="closePayModal">
          <i class="mdi mdi-shield-check me-1"></i>View My Policies
        </button>
      </div>
    </div>
  </div>

  <!-- ===================== CLAIM MODAL ===================== -->
  <div v-if="showClaimModal" class="ins-modal-overlay" @click.self="closeClaim">
    <div class="ins-modal">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;"><i class="mdi mdi-file-alert me-2" style="color:#FF6B6B;"></i>Submit Claim</h5>
        <button class="ins-x" @click="closeClaim">&times;</button>
      </div>

      <div v-if="selectedPolicy" class="ins-modal-prod mb-3">
        <div class="ins-product-name">{{ selectedPolicy.product_name }}</div>
        <div class="ins-provider-name">Policy {{ selectedPolicy.policy_number }} · Cover KES {{ Number(selectedPolicy.covered_value||0).toLocaleString() }}</div>
      </div>

      <div class="mb-3">
        <label class="ins-label">Claim type <span style="color:#FF6B6B;">*</span></label>
        <select v-model="claimForm.claimType" class="form-select ins-input">
          <option value="">Select claim type</option>
          <option value="death">Death</option>
          <option value="disease">Disease</option>
          <option value="accident">Accident</option>
          <option value="theft">Theft</option>
          <option value="drought">Drought</option>
          <option value="transport_damage">Transport damage</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="ins-label">Incident date <span style="color:#FF6B6B;">*</span></label>
        <input type="date" v-model="claimForm.incidentDate" :max="today" class="form-control ins-input" />
      </div>

      <div class="mb-3">
        <label class="ins-label">What happened? <span style="color:#FF6B6B;">*</span></label>
        <textarea v-model="claimForm.incidentDescription" rows="4" class="form-control ins-input" placeholder="Describe the incident — when, where, animals affected, vet diagnosis if any..."></textarea>
      </div>

      <div class="mb-3">
        <label class="ins-label">Claimed amount (KES)</label>
        <input type="number" v-model.number="claimForm.claimedAmount" class="form-control ins-input" :placeholder="'Up to ' + Number(selectedPolicy?.covered_value||0).toLocaleString()" />
        <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:4px;">Leave blank to claim the full covered value.</div>
      </div>

      <div v-if="claimError" class="ins-warn mb-2"><i class="mdi mdi-alert-circle me-1"></i>{{ claimError }}</div>

      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary flex-fill" @click="closeClaim" :disabled="submitting">Cancel</button>
        <button class="btn btn-warning flex-fill" :disabled="!canClaim || submitting" @click="submitClaim">
          <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Submitting...</span>
          <span v-else><i class="mdi mdi-send me-1"></i>Submit Claim</span>
        </button>
      </div>

      <p style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:10px;margin-bottom:0;">
        After submission the provider has up to 14 working days to review. You'll be notified when the status changes.
      </p>
    </div>
  </div>

  <!-- ===================== APPLY FOR FINANCE MODAL ===================== -->
  <div v-if="showFinanceApply" class="ins-modal-overlay" @click.self="closeFinanceApply">
    <div class="ins-modal">
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 class="fw-bold mb-1" style="color:var(--fm-h,#F5F0E8);">Apply for finance</h5>
          <div style="color:var(--fm-s,#6B6356);font-size:12px;">{{ financeApplyProduct && financeApplyProduct.name }} <span v-if="financeApplyProduct">· {{ financeApplyProduct.provider_name }}</span></div>
        </div>
        <button class="btn-close btn-close-white" @click="closeFinanceApply"></button>
      </div>

      <div v-if="financeApplyProduct && financeApplyProduct.provider_kyc_status === 'pending'" class="ins-pending-banner mb-3">
        <i class="mdi mdi-clock-outline me-1"></i>This lender is still pending verification by Flockr. You can submit but verify their identity independently before sharing sensitive documents.
      </div>

      <div class="mb-2"><label class="ins-modal-label">Amount you want to borrow (KES) *</label>
        <input v-model.number="financeApplyForm.amount" type="number" class="ins-modal-input w-100" :placeholder="financeAmountPlaceholder" />
        <div v-if="financeAmountWarning" style="color:#FF6B6B;font-size:11px;margin-top:4px;">{{ financeAmountWarning }}</div>
      </div>
      <div class="row g-2 mb-2">
        <div class="col-6"><label class="ins-modal-label">Tenure (months)</label><input v-model.number="financeApplyForm.tenure" type="number" class="ins-modal-input w-100" :placeholder="financeTenurePlaceholder" /></div>
        <div class="col-6"><label class="ins-modal-label">Purpose</label><input v-model="financeApplyForm.purpose" class="ins-modal-input w-100" placeholder="e.g. Buy 5 dairy cows" /></div>
      </div>
      <div class="mb-3"><label class="ins-modal-label">Notes to lender (optional)</label>
        <textarea v-model="financeApplyForm.notes" class="ins-modal-input w-100" rows="2" placeholder="Anything the lender should know about your operation."></textarea>
      </div>

      <div v-if="financeApplyError" class="alert alert-danger py-2" style="font-size:12px;">{{ financeApplyError }}</div>

      <button class="btn btn-warning w-100" @click="submitFinanceApply" :disabled="!canSubmitFinanceApply || financeApplySubmitting">
        <span v-if="financeApplySubmitting"><span class="spinner-border spinner-border-sm me-1"></span>Submitting...</span>
        <span v-else><i class="mdi mdi-send me-1"></i>Submit application</span>
      </button>
      <p style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:10px;margin-bottom:0;">
        The lender reviews and replies through your inbox. Approval and disbursement happen directly between you and the lender.
      </p>
    </div>
  </div>

</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] .ins-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .ins-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .ins-card,
html[data-theme="light"] .ins-policy-card,
html[data-theme="light"] .ins-modal,
html[data-theme="light"] .ins-modal-prod,
html[data-theme="light"] .ins-premium-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .ins-input{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .ins-chip,
html[data-theme="light"] .ins-tab{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#555!important}
html[data-theme="light"] .ins-chip.active,
html[data-theme="light"] .ins-tab.active{background:rgba(191,161,100,.1)!important;border-color:#BFA164!important;color:#BFA164!important}
</style>

<style scoped>
.ins-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ins-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ins-container{padding-top:80px;padding-bottom:60px;max-width:1100px}

/* Tabs */
.ins-tabs{display:flex;gap:8px;border-bottom:1px solid var(--fm-br,#1E1E1E);padding-bottom:0}
.ins-tab{padding:8px 16px;border-radius:8px 8px 0 0;border:1px solid var(--fm-br,#1E1E1E);border-bottom:none;background:transparent;color:var(--fm-m,#A09888);font-size:13px;font-weight:600;cursor:pointer;position:relative;display:inline-flex;align-items:center}
.ins-tab:hover{color:#BFA164}
.ins-tab.active{background:rgba(191,161,100,.1);border-color:#BFA164;color:#BFA164}
.ins-tab-badge{margin-left:6px;background:#BFA164;color:#1A1A1A;font-size:10px;font-weight:700;padding:1px 6px;border-radius:10px}

/* Chips */
.ins-chip{padding:6px 14px;border-radius:20px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg1,#141414);color:var(--fm-m,#A09888);font-size:12px;cursor:pointer;transition:all .2s}
.ins-chip:hover{border-color:#BFA164;color:#BFA164}
.ins-chip.active{background:rgba(191,161,100,.1);border-color:#BFA164;color:#BFA164;font-weight:600}

/* Cards */
.ins-card,.ins-policy-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px;transition:all .2s;height:100%;display:flex;flex-direction:column}
.ins-card:hover{border-color:#BFA164;transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.2)}
.ins-provider-logo{width:44px;height:44px;border-radius:10px;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:700;color:#BFA164;overflow:hidden;flex-shrink:0}
.ins-provider-logo img{width:100%;height:100%;object-fit:cover}
.ins-product-name{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px}
.ins-provider-name{color:var(--fm-m,#A09888);font-size:11px}
.ins-type-tag{font-size:10px;padding:3px 10px;border-radius:6px;font-weight:600;background:rgba(191,161,100,.1);color:#BFA164;text-transform:capitalize;white-space:nowrap}
.ins-desc{color:var(--fm-m,#A09888);font-size:12px;margin-bottom:12px;line-height:1.5}

.ins-coverage{font-size:11px}
.ins-coverage-label{color:var(--fm-s,#6B6356);font-weight:600;font-size:10px;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px}
.ins-pill{font-size:10px;padding:2px 8px;border-radius:6px;text-transform:capitalize}
.ins-pill.ok{background:rgba(80,200,120,.1);color:#50C878}
.ins-pill.bad{background:rgba(255,107,107,.1);color:#FF6B6B}
.ins-pending-banner{font-size:11px;color:#f1b44c;background:rgba(241,180,76,.08);border:1px solid rgba(241,180,76,.2);border-radius:8px;padding:6px 10px;margin-bottom:10px}

.ins-stats,.ins-policy-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;padding:10px;background:var(--fm-bg2,#0F0F0F);border-radius:8px}
.ins-stat-label{color:var(--fm-s,#6B6356);font-size:9px;text-transform:uppercase;letter-spacing:.5px;font-weight:600}
.ins-stat-val{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px;margin-top:2px}

/* Status pills */
.ins-status{font-size:10px;padding:3px 10px;border-radius:6px;font-weight:600;text-transform:uppercase;letter-spacing:.3px;white-space:nowrap;background:var(--fm-bg3,#1E1E1E);color:var(--fm-m,#A09888)}
.ins-status.st-pending{background:rgba(241,180,76,.15);color:#f1b44c}
.ins-status.st-active{background:rgba(80,200,120,.15);color:#50C878}
.ins-status.st-claimed{background:rgba(191,161,100,.15);color:#BFA164}
.ins-status.st-expired,.ins-status.st-lapsed{background:rgba(160,152,136,.15);color:#A09888}
.ins-status.st-cancelled,.ins-status.st-rejected,.ins-status.st-withdrawn{background:rgba(255,107,107,.15);color:#FF6B6B}
.ins-status.st-submitted,.ins-status.st-under_review{background:rgba(241,180,76,.15);color:#f1b44c}
.ins-status.st-approved,.ins-status.st-paid{background:rgba(80,200,120,.15);color:#50C878}

.ins-review{margin-top:8px;padding:8px;border-left:3px solid #BFA164;background:var(--fm-bg2,#0F0F0F);font-size:11px;color:var(--fm-m,#A09888);border-radius:0 6px 6px 0}

/* Modal */
.ins-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);backdrop-filter:blur(4px);z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px}
.ins-modal{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:24px;max-width:480px;width:100%;max-height:90vh;overflow-y:auto}
.ins-modal-prod{padding:10px 12px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px}
.ins-x{background:transparent;border:none;color:var(--fm-s,#6B6356);font-size:24px;line-height:1;cursor:pointer}
.ins-x:hover{color:#FF6B6B}

.ins-label{color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;margin-bottom:6px;display:block}
.ins-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.ins-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}

.ins-premium-card{background:var(--fm-bg2,#0F0F0F);border:1px solid #BFA164;border-radius:10px;padding:14px}

.ins-warn{color:#FF6B6B;font-size:12px;background:rgba(255,107,107,.08);border:1px solid rgba(255,107,107,.2);border-radius:6px;padding:6px 10px}

/* Pay-premium modal */
.ins-pay-modal{max-width:480px}
.ins-pay-info-icon{width:64px;height:64px;border-radius:50%;background:rgba(191,161,100,.12);color:#BFA164;display:flex;align-items:center;justify-content:center;font-size:32px;margin:0 auto 14px}
.ins-pay-card{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:14px 16px}
.ins-pay-row{display:flex;justify-content:space-between;align-items:center;padding:5px 0;font-size:12px}
.ins-pay-label{color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.5px;font-weight:600;font-size:10px}
.ins-pay-val{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;text-align:right}
.ins-pay-val.mono{font-family:monospace}

.ins-pay-state-bad{margin-top:10px;padding:8px 12px;border-radius:6px;background:rgba(255,107,107,.08);border:1px solid rgba(255,107,107,.25);color:#FF6B6B;font-size:12px}
.ins-paybill-fallback{margin-top:6px;padding:10px 12px;border-radius:8px;background:rgba(191,161,100,.08);border:1px dashed rgba(191,161,100,.35);color:var(--fm-m,#A09888);font-size:12px;line-height:1.6}

.ins-pay-pulse{text-align:center;padding:20px 0 6px}
.ins-pulse{position:relative;width:120px;height:120px;margin:0 auto 14px;display:flex;align-items:center;justify-content:center}
.ins-pulse-core{width:64px;height:64px;border-radius:50%;background:rgba(80,200,120,.15);color:#50C878;display:flex;align-items:center;justify-content:center;font-size:32px;z-index:2}
.ins-pulse-ring{position:absolute;width:100%;height:100%;border-radius:50%;border:3px solid #50C878;opacity:.6;animation:insPulse 1.4s ease-out infinite}
@keyframes insPulse{
  0%   { transform:scale(.6); opacity:.7; }
  80%  { transform:scale(1.0); opacity:0; }
  100% { transform:scale(1.0); opacity:0; }
}
.ins-pay-success{text-align:center;padding:10px 0}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .ins-container { padding-top: 70px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .ins-card { padding: 12px !important; border-radius: 10px !important; }
  .ins-product-card { padding: 14px !important; }
  .ins-tier-pill { font-size: 10px; padding: 3px 8px; }
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";

export default {
  name: "InsurancePage",
  data(){return{
    darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
    themeObserver: null,

    tab: 'browse',
    typeFilter: '',

    products: [],
    loadingProducts: false,

    policies: [],
    loadingPolicies: false,

    claims: [],
    loadingClaims: false,

    animals: [],
    loadingAnimals: false,
    animalsLoaded: false,

    showPurchaseModal: false,
    selectedProduct: null,
    purchaseForm: { coveredValue: null, animalId: '' },
    purchaseError: '',

    showClaimModal: false,
    selectedPolicy: null,
    claimForm: { claimType: '', incidentDate: '', incidentDescription: '', claimedAmount: null },
    claimError: '',

    submitting: false,

    // Pay-premium modal (real STK)
    showPayModal: false,
    payPolicy: null,
    payPhone: '',
    payState: 'idle',   // idle | submitting | stk_sent | success | cancelled | failed | timeout
    payCheckoutId: '',
    payShortcode: '',
    payAccountRef: '',
    payAmount: 0,
    payError: '',
    payPollTimer: null,

    today: new Date().toISOString().slice(0,10),

    // Finance browse
    financeProducts: [],
    loadingFinance: false,
    financeTypeFilter: '',

    // Finance applications (farmer side)
    myFinanceApps: [],
    loadingMyFinanceApps: false,

    // Apply modal
    showFinanceApply: false,
    financeApplyProduct: null,
    financeApplyForm: { amount: null, tenure: null, purpose: '', notes: '' },
    financeApplyError: '',
    financeApplySubmitting: false
  }},
  computed:{
    themePageStyle(){
      return this.darkMode
        ? 'background:#0A0A0A;min-height:100vh;color:#F5F0E8;'
        : 'background:#F7F5F2;min-height:100vh;color:#1A1A1A;';
    },
    filteredProducts(){
      if (!this.typeFilter) return this.products;
      var t = this.typeFilter;
      return this.products.filter(function(p){ return p.insurance_type === t; });
    },
    calculatedPremium(){
      if (!this.selectedProduct) return 0;
      var p = this.selectedProduct;
      if (p.flat_premium) return Number(p.flat_premium);
      var v = Number(this.purchaseForm.coveredValue || 0);
      if (!v) return 0;
      return Math.round(v * (Number(p.premium_rate_pct||0) / 100));
    },
    valuePlaceholder(){
      if (!this.selectedProduct) return '';
      var min = Number(this.selectedProduct.min_animal_value||0);
      var max = Number(this.selectedProduct.max_animal_value||0);
      if (min && max) return min.toLocaleString() + ' – ' + max.toLocaleString();
      if (min) return 'Minimum ' + min.toLocaleString();
      return 'Enter animal value';
    },
    valueWarning(){
      if (!this.selectedProduct || !this.purchaseForm.coveredValue) return '';
      var v = Number(this.purchaseForm.coveredValue);
      var min = Number(this.selectedProduct.min_animal_value||0);
      var max = Number(this.selectedProduct.max_animal_value||0);
      if (min && v < min) return 'Below the minimum cover (KES ' + min.toLocaleString() + ').';
      if (max && v > max) return 'Above the maximum cover (KES ' + max.toLocaleString() + ').';
      return '';
    },
    canPurchase(){
      if (!this.selectedProduct) return false;
      var v = Number(this.purchaseForm.coveredValue||0);
      return v > 0 && !this.valueWarning;
    },
    canClaim(){
      return this.claimForm.claimType && this.claimForm.incidentDate && this.claimForm.incidentDescription;
    },
    canPay(){
      var p = (this.payPhone || '').replace(/[^0-9]/g, '');
      // Accepts 07XXXXXXXX (10), 7XXXXXXXX (9), or 254XXXXXXXXX (12)
      return p.length === 9 || p.length === 10 || p.length === 12;
    },
    filteredFinanceProducts(){
      if (!this.financeTypeFilter) return this.financeProducts;
      var t = this.financeTypeFilter;
      return this.financeProducts.filter(function(p){ return p.product_type === t; });
    },
    financeAmountPlaceholder(){
      var p = this.financeApplyProduct; if (!p) return '';
      var min = Number(p.min_amount||0), max = Number(p.max_amount||0);
      if (min && max) return min.toLocaleString() + ' – ' + max.toLocaleString();
      if (min) return 'Minimum ' + min.toLocaleString();
      return 'Enter amount';
    },
    financeTenurePlaceholder(){
      var p = this.financeApplyProduct; if (!p) return '';
      if (p.tenure_min_months || p.tenure_max_months) return (p.tenure_min_months||1) + '–' + (p.tenure_max_months||'') + ' months';
      return '';
    },
    financeAmountWarning(){
      var p = this.financeApplyProduct; if (!p || !this.financeApplyForm.amount) return '';
      var v = Number(this.financeApplyForm.amount);
      var min = Number(p.min_amount||0), max = Number(p.max_amount||0);
      if (min && v < min) return 'Below the minimum (KES ' + min.toLocaleString() + ').';
      if (max && v > max) return 'Above the maximum (KES ' + max.toLocaleString() + ').';
      return '';
    },
    canSubmitFinanceApply(){
      return this.financeApplyProduct && Number(this.financeApplyForm.amount||0) > 0 && !this.financeAmountWarning;
    }
  },
  mounted(){
    this._applyThemeOnce();
    var self = this;
    this.themeObserver = new MutationObserver(function(){
      try { self._applyThemeOnce(); } catch(e) {}
    });
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    if (!sessionStorage.getItem('userId')) { this.$router.push('/login'); return; }
    var path = (this.$route && this.$route.path) || '';
    if (path.indexOf('/finance') === 0) { this.tab = 'finance'; this.loadFinanceProducts(); }
    else { this.loadProducts(); }
  },
  beforeUnmount(){
    if (this.themeObserver) { this.themeObserver.disconnect(); this.themeObserver = null; }
  },
  methods:{
    _applyThemeOnce(){
      var current = document.documentElement.getAttribute('data-theme');
      var desired = (localStorage.getItem('flockr_theme') || current || 'dark');
      if (current !== desired) document.documentElement.setAttribute('data-theme', desired);
      this.darkMode = desired !== 'light';
    },
    typeLabel(t){
      var map = {
        livestock:'Livestock',
        comprehensive:'Comprehensive',
        transit:'Transit',
        theft:'Theft',
        pet:'Pet',
        index_based:'Index-based'
      };
      return map[t] || (t || '').replace(/_/g,' ');
    },
    humanize(s){ return (s||'').replace(/_/g,' ').replace(/\b\w/g, function(c){return c.toUpperCase()}); },
    coverageOf(p){
      var out = { covers:[], excludes:[] };
      try {
        var d = typeof p.coverage_details === 'string' ? JSON.parse(p.coverage_details||'{}') : (p.coverage_details||{});
        if (Array.isArray(d.covers))   out.covers   = d.covers;
        if (Array.isArray(d.excludes)) out.excludes = d.excludes;
      } catch(e){}
      return out;
    },

    async loadProducts(){
      this.loadingProducts = true;
      try {
        var r = await executeJson({ function:'getInsuranceProducts' }, flocker);
        if (r.data && r.data.success) this.products = r.data.data.products || [];
      } catch(e){ console.error('loadProducts',e); }
      this.loadingProducts = false;
    },

    // ----- Finance browse -----
    imgUrl(u){ if (!u) return ''; if (String(u).indexOf('http') === 0) return u; return uploads + String(u).replace(/^uploads\//, ''); },
    financeTypeLabel(t){
      var m = { livestock_asset:'Livestock Asset', working_capital:'Working Capital', lease_to_own:'Lease-to-Own', savings:'Savings' };
      return m[t] || (t||'').replace(/_/g,' ');
    },
    instLabel(t){ var m = { bank:'Bank', mfi:'MFI', sacco:'SACCO', fintech:'Fintech', other:'Other' }; return m[t] || (t||''); },
    parseRequirements(v){ if (Array.isArray(v)) return v; try { var a = JSON.parse(v||'[]'); return Array.isArray(a) ? a : []; } catch(e){ return []; } },
    async loadFinanceProducts(){
      this.loadingFinance = true;
      try {
        var r = await executeJson({ function:'getFinanceProducts' }, flocker);
        if (r.data && r.data.success) this.financeProducts = r.data.data.products || [];
      } catch(e){ console.error('loadFinanceProducts', e); }
      this.loadingFinance = false;
    },
    async loadMyFinanceApps(){
      var userId = sessionStorage.getItem('userId');
      if (!userId) return;
      this.loadingMyFinanceApps = true;
      try {
        var r = await executeJson({ function:'getMyFinanceApplications', applicantId: userId }, flocker);
        if (r.data && r.data.success) this.myFinanceApps = r.data.data.applications || [];
      } catch(e){ console.error('loadMyFinanceApps', e); }
      this.loadingMyFinanceApps = false;
    },
    openFinanceApply(p){
      if (!sessionStorage.getItem('userId')) { this.$router.push('/login'); return; }
      this.financeApplyProduct = p;
      this.financeApplyForm = { amount: p.min_amount ? Number(p.min_amount) : null, tenure: p.tenure_min_months ? Number(p.tenure_min_months) : null, purpose: '', notes: '' };
      this.financeApplyError = '';
      this.showFinanceApply = true;
    },
    closeFinanceApply(){ this.showFinanceApply = false; this.financeApplyProduct = null; this.financeApplyError = ''; },
    async submitFinanceApply(){
      if (!this.canSubmitFinanceApply) return;
      this.financeApplySubmitting = true;
      this.financeApplyError = '';
      try {
        var r = await executeJson({
          function: 'applyForFinance',
          applicantId: sessionStorage.getItem('userId'),
          productId: this.financeApplyProduct.id,
          amountRequested: this.financeApplyForm.amount,
          tenureMonths: this.financeApplyForm.tenure || '',
          purpose: this.financeApplyForm.purpose || '',
          applicantNotes: this.financeApplyForm.notes || ''
        }, flocker);
        if (r.data && r.data.success) {
          this.closeFinanceApply();
          alert('Application submitted. The lender will respond in your Finance Applications tab and inbox.');
          this.tab = 'finance-apps';
          await this.loadMyFinanceApps();
        } else {
          this.financeApplyError = (r.data && r.data.message) || 'Could not submit application.';
        }
      } catch(e){ this.financeApplyError = 'Network error. Please retry.'; }
      this.financeApplySubmitting = false;
    },

    async loadPolicies(){
      var userId = sessionStorage.getItem('userId');
      if (!userId) return;
      this.loadingPolicies = true;
      try {
        var r = await executeJson({ function:'getMyInsurancePolicies', userId: userId }, flocker);
        if (r.data && r.data.success) this.policies = r.data.data.policies || [];
      } catch(e){ console.error('loadPolicies',e); }
      this.loadingPolicies = false;
    },

    async loadClaims(){
      var userId = sessionStorage.getItem('userId');
      if (!userId) return;
      this.loadingClaims = true;
      try {
        var r = await executeJson({ function:'getMyInsuranceClaims', userId: userId }, flocker);
        if (r.data && r.data.success) this.claims = r.data.data.claims || [];
      } catch(e){ console.error('loadClaims',e); }
      this.loadingClaims = false;
    },

    async loadAnimalsOnce(){
      if (this.animalsLoaded) return;
      this.loadingAnimals = true;
      try {
        var userId = sessionStorage.getItem('userId');
        var fr = await executeJson({ function:'getMyFarms', ownerId: userId }, flocker);
        var farms = (fr.data && fr.data.success && fr.data.data.farms) ? fr.data.data.farms : [];
        var all = [];
        for (var i = 0; i < farms.length; i++) {
          try {
            var ar = await executeJson({ function:'getFarmAnimals', farmId: farms[i].id }, flocker);
            if (ar.data && ar.data.success) all = all.concat(ar.data.data.animals || []);
          } catch(e){}
        }
        this.animals = all;
      } catch(e){}
      this.loadingAnimals = false;
      this.animalsLoaded = true;
    },

    openPurchase(p){
      this.selectedProduct = p;
      this.purchaseForm = { coveredValue: null, animalId: '' };
      this.purchaseError = '';
      this.showPurchaseModal = true;
      this.loadAnimalsOnce();
    },
    closePurchase(){ this.showPurchaseModal = false; this.selectedProduct = null; },

    async submitPurchase(){
      if (!this.canPurchase || this.submitting) return;
      this.submitting = true; this.purchaseError = '';
      try {
        var params = {
          function: 'purchaseInsurance',
          userId: sessionStorage.getItem('userId'),
          productId: this.selectedProduct.id,
          coveredValue: this.purchaseForm.coveredValue
        };
        if (this.purchaseForm.animalId) params.animalId = this.purchaseForm.animalId;
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          alert('Policy ' + (d.policyNumber || 'created') + ' — premium KES ' + Number(d.premiumAmount||0).toLocaleString() + '. Pay the premium from My Policies to activate.');
          this.closePurchase();
          this.tab = 'policies';
          this.loadPolicies();
        } else {
          this.purchaseError = (r.data && r.data.message) || 'Could not create policy.';
        }
      } catch(e){
        this.purchaseError = (e && e.message) || 'Network error. Please try again.';
      }
      this.submitting = false;
    },

    payPremium(po){
      this.payPolicy = po;
      this.payPhone = sessionStorage.getItem('userPhone') || '';
      this.payState = 'idle';
      this.payError = '';
      this.payCheckoutId = '';
      this.payShortcode = '';
      this.payShortcodeType = 'paybill';
      this.payAccountRef = '';
      this.payAmount = Number(po.premium_amount || 0);
      this.showPayModal = true;
    },
    closePayModal(){
      this.showPayModal = false;
      this.payPolicy = null;
      this.stopPayPolling();
      // If the payment succeeded, refresh the policy list so the row flips to active
      if (this.payState === 'success') this.loadPolicies();
      this.payState = 'idle';
    },
    async submitPay(){
      if (!this.canPay) return;
      this.payState = 'submitting'; this.payError = '';
      try {
        var r = await executeJson({
          function: 'payInsurancePremium',
          userId: sessionStorage.getItem('userId'),
          policyId: this.payPolicy.id,
          phone: this.payPhone
        }, flocker);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          this.payCheckoutId    = d.checkoutRequestId || '';
          this.payShortcode     = d.shortcode || '';
          this.payShortcodeType = d.shortcodeType || 'paybill';
          this.payAccountRef    = d.accountRef || '';
          this.payAmount        = Number(d.amount || this.payAmount);
          this.payState = 'stk_sent';
          this.startPayPolling();
        } else {
          this.payState = 'failed';
          this.payError = (r.data && r.data.message) || 'Could not send STK prompt.';
        }
      } catch(e){
        this.payState = 'failed';
        this.payError = (e && e.message) || 'Network error. Please try again.';
      }
    },
    startPayPolling(){
      this.stopPayPolling();
      var self = this;
      var ticks = 0;
      this.payPollTimer = setInterval(function(){
        ticks++;
        // After ~90s of polling without resolution, surface a soft timeout. The callback may
        // still arrive — we just stop bothering the user every 4s.
        if (ticks > 22) { self.payState = 'timeout'; self.stopPayPolling(); return; }
        self.checkPaymentStatus();
      }, 4000);
    },
    stopPayPolling(){
      if (this.payPollTimer) { clearInterval(this.payPollTimer); this.payPollTimer = null; }
    },
    async checkPaymentStatus(){
      if (!this.payCheckoutId) return;
      try {
        var r = await executeJson({
          function: 'checkInsurancePaymentStatus',
          userId: sessionStorage.getItem('userId'),
          policyId: this.payPolicy && this.payPolicy.id,
          checkoutRequestId: this.payCheckoutId
        }, flocker);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          var s = d.status || 'pending';
          if (s === 'completed' || (d.policy && d.policy.status === 'active')) {
            this.payState = 'success';
            this.stopPayPolling();
          } else if (s === 'cancelled') {
            this.payState = 'cancelled'; this.stopPayPolling();
          } else if (s === 'failed' || s === 'timeout') {
            this.payState = s; this.payError = d.failureReason || ''; this.stopPayPolling();
          }
        }
      } catch(e){ /* poll silently — keep trying */ }
    },

    openClaim(po){
      this.selectedPolicy = po;
      this.claimForm = { claimType:'', incidentDate: this.today, incidentDescription:'', claimedAmount: null };
      this.claimError = '';
      this.showClaimModal = true;
    },
    closeClaim(){ this.showClaimModal = false; this.selectedPolicy = null; },

    async submitClaim(){
      if (!this.canClaim || this.submitting) return;
      this.submitting = true; this.claimError = '';
      try {
        var params = {
          function: 'submitInsuranceClaim',
          userId: sessionStorage.getItem('userId'),
          policyId: this.selectedPolicy.id,
          claimType: this.claimForm.claimType,
          incidentDate: this.claimForm.incidentDate,
          incidentDescription: this.claimForm.incidentDescription
        };
        if (this.claimForm.claimedAmount) params.claimedAmount = this.claimForm.claimedAmount;
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success) {
          alert('Claim ' + (r.data.data.claimNumber || 'submitted') + '. The provider will review within 14 working days.');
          this.closeClaim();
          this.tab = 'claims';
          this.loadClaims();
          this.loadPolicies();
        } else {
          this.claimError = (r.data && r.data.message) || 'Could not submit claim.';
        }
      } catch(e){
        this.claimError = (e && e.message) || 'Network error. Please try again.';
      }
      this.submitting = false;
    }
  }
};
</script>
